import { vatPercentToStoredFraction } from './sale-vat';

type SaleTotalsLine = {
  quantity: number;
  unitPrice: number;
  discountCost?: number;
};

type SaleNetTotalInput = {
  saleProducts: SaleTotalsLine[];
  vatPercentage: number;
  shipmentCost: number;
  invoiceDiscountCost: number;
};

const roundToTwoDecimals = (value: number) => Number(value.toFixed(2));

// The sale total exactly as the API's createSales computes it (salesCopy.service.ts), so a Full
// Cash payment equals the saved totalAmount. The API skips a payment larger than the total, which
// left sales with line discounts saved as DUE.
export const calculateSaleNetTotal = ({
  saleProducts,
  vatPercentage,
  shipmentCost,
  invoiceDiscountCost,
}: SaleNetTotalInput): number => {
  const subtotalAfterLineDiscounts = saleProducts.reduce(
    (total, line) =>
      total +
      roundToTwoDecimals(
        line.quantity * line.unitPrice - roundToTwoDecimals(line.discountCost || 0),
      ),
    0,
  );
  const vatRate = vatPercentToStoredFraction(vatPercentage);

  return roundToTwoDecimals(
    subtotalAfterLineDiscounts * vatRate +
      subtotalAfterLineDiscounts +
      roundToTwoDecimals(shipmentCost || 0) -
      roundToTwoDecimals(invoiceDiscountCost || 0),
  );
};

// The parts of that total for display: lines after their discounts, the VAT, and the total. VAT is
// whatever the total adds beyond lines, shipping and discount, so the shown parts always add up to
// the total the API saves, to the paisa.
export const calculateSaleTotals = (input: SaleNetTotalInput) => {
  const linesSubTotal = roundToTwoDecimals(
    input.saleProducts.reduce(
      (total, line) =>
        total +
        roundToTwoDecimals(
          line.quantity * line.unitPrice - roundToTwoDecimals(line.discountCost || 0),
        ),
      0,
    ),
  );
  const netTotal = calculateSaleNetTotal(input);
  const vatAmount = roundToTwoDecimals(
    netTotal -
      linesSubTotal -
      roundToTwoDecimals(input.shipmentCost || 0) +
      roundToTwoDecimals(input.invoiceDiscountCost || 0),
  );
  return { linesSubTotal, vatAmount, netTotal };
};
